import React, { useMemo } from "react";
import { diatonicIndex, parsePitch } from "@/lib/music";
import { buildScore, quarterLen } from "@/lib/playalongScore";
import { beamGroups, fitBeam, groupUp } from "@/lib/beams";

const W = 760, SYS_H = 150, TOP = 50, GAP = 7, LEFT = 60;
const REF = { treble: 5 * 7 + 3, percussion: 5 * 7 + 3 };
const yOf = (p, clef) => TOP + (REF[clef] - diatonicIndex(p)) * GAP;
const lineYs = [0, 1, 2, 3, 4].map((i) => TOP + i * GAP * 2);

const Ledger = ({ x, y }) => {
  const out = [];
  for (let ly = TOP - 14; ly >= y - 1; ly -= 14) out.push(ly);
  for (let ly = TOP + 70; ly <= y + 1; ly += 14) out.push(ly);
  return out.map((ly) => <line key={ly} x1={x - 11} x2={x + 11} y1={ly} y2={ly} stroke="var(--staff-line)" strokeWidth="1.2" />);
};

// Beam quavers/semiquavers per beat group; semiquaver pairs get a second beam (or a stub).
const barBeams = (events, clef, sub, drums, xAt, beats) => {
  const items = events.map((ev) => { const len = quarterLen(ev.len, sub); return { pos: quarterLen(ev.step, sub), len, beam: !ev.rest && [0.5, 0.25, 0.75].includes(len) }; });
  const stems = {}, beams = [];
  beamGroups(items, sub === 3 ? 1.5 : beats === 3 ? 1 : 2).forEach((g) => {
    const geo = g.map((i) => { const ys = events[i].pitches.map((p) => yOf(p, clef)); return { top: Math.min(...ys), bottom: Math.max(...ys) }; });
    const up = drums || groupUp(geo, TOP + 28);
    const pts = g.map((i, k) => ({ ...geo[k], x: xAt(events[i].step) + (up ? 6 : -6) }));
    const line = drums ? { at: () => TOP - 30 } : fitBeam(pts, up, 32);
    g.forEach((i, k) => { stems[i] = { up, end: line.at(pts[k].x) }; });
    const seg = (a, b, off) => beams.push({ x0: a, y0: line.at(a) + off, x1: b, y1: line.at(b) + off, up });
    seg(pts[0].x, pts[pts.length - 1].x, 0);
    const off = up ? 7 : -7;
    g.forEach((i, k) => {
      if (items[i].len !== 0.25) return;
      const nxt = g[k + 1] !== undefined && items[g[k + 1]].len === 0.25;
      const prv = k > 0 && items[g[k - 1]].len === 0.25;
      if (nxt) seg(pts[k].x, pts[k + 1].x, off);
      else if (!prv) seg(k > 0 ? pts[k].x - 9 : pts[k].x, k > 0 ? pts[k].x : pts[k].x + 9, off);
    });
  });
  return { stems, beams };
};

const Note = ({ ev, x, clef, sub, color, drums, bm }) => {
  const q = quarterLen(ev.len, sub);
  if (ev.rest) {
    const dot = [3, 1.5, 0.75].includes(q) && <circle cx={x + 11} cy={TOP + 18} r={2.2} fill={color} />;
    // Whole bar's rest hangs off the 4th line; minim rest sits on the 3rd line.
    if (ev.bar || q >= 4) return <g data-testid="score-rest-bar"><rect x={x - 7} y={TOP + 14} width={14} height={6} fill={color} /></g>;
    if (q >= 2) return <g data-testid="score-rest-minim"><rect x={x - 7} y={TOP + 22} width={14} height={6} fill={color} />{dot}</g>;
    if (q >= 1) return <g data-testid="score-rest-crotchet"><path d={`M${x - 3} ${TOP + 12} l6 8 l-6 6 l6 8 l-7 -2`} stroke={color} strokeWidth="2.2" fill="none" />{dot}</g>;
    const semi = q < 0.5;
    return (
      <g data-testid={semi ? "score-rest-semiquaver" : "score-rest-quaver"}>
        <circle cx={x - 3} cy={TOP + 19} r={2.6} fill={color} />
        {semi && <circle cx={x - 5} cy={TOP + 27} r={2.6} fill={color} />}
        <path d={`M${x - 3} ${TOP + 21} q4 2 7 -4 l${semi ? -8 : -6} ${semi ? 26 : 19}`} stroke={color} strokeWidth="1.8" fill="none" />
        {semi && <path d={`M${x - 5} ${TOP + 29} q4 2 7 -3`} stroke={color} strokeWidth="1.8" fill="none" />}
        {dot}
      </g>
    );
  }
  const hollow = q >= 2;
  const ys = ev.pitches.map((p) => yOf(p, clef));
  const top = Math.min(...ys), bottom = Math.max(...ys);
  const up = bm ? bm.up : drums || bottom > TOP + 28;
  return (
    <g opacity={ev.ghost ? 0.45 : 1}>
      {ev.pitches.map((p, k) => {
        const y = ys[k];
        const acc = parsePitch(p)?.accidental;
        const x2 = x + (k > 0 && Math.abs(ys[k] - ys[k - 1]) < GAP + 1 ? 13 : 0);
        return (
          <g key={k}>
            <Ledger x={x2} y={y} />
            {acc && !drums && <text x={x2 - 22} y={y + 5} fontSize="18" fill={color} fontFamily="serif">{acc === "#" ? "♯" : "♭"}</text>}
            {ev.heads?.[k] === "x" ? (
              <g stroke={color} strokeWidth="1.8"><line x1={x2 - 5} y1={y - 5} x2={x2 + 5} y2={y + 5} /><line x1={x2 - 5} y1={y + 5} x2={x2 + 5} y2={y - 5} /></g>
            ) : (
              <ellipse cx={x2} cy={y} rx="6.5" ry="4.8" transform={`rotate(-20 ${x2} ${y})`} fill={hollow ? "var(--staff-hollow)" : color} stroke={color} strokeWidth="1.6" />
            )}
          </g>
        );
      })}
      {[1.5, 3, 0.75].includes(q) && <circle cx={x + 11} cy={bottom - 2} r="1.8" fill={color} />}
      {q < 4 && (up ? <line x1={x + 6} x2={x + 6} y1={bottom} y2={bm ? bm.end : drums ? TOP - 30 : top - 32} stroke={color} strokeWidth="1.5" />
        : <line x1={x - 6} x2={x - 6} y1={top} y2={bm ? bm.end : bottom + 32} stroke={color} strokeWidth="1.5" />)}
      {!bm && q <= 0.75 && <path d={up ? (drums ? `M${x + 6} ${TOP - 30} q8 8 3 15` : `M${x + 6} ${top - 32} q10 10 4 22`) : `M${x - 6} ${bottom + 32} q10 -10 4 -22`} stroke={color} strokeWidth="1.6" fill="none" />}
      {!bm && q === 0.25 && <path d={up ? (drums ? `M${x + 6} ${TOP - 24} q8 8 3 15` : `M${x + 6} ${top - 24} q10 10 4 22`) : `M${x - 6} ${bottom + 24} q10 -10 4 -22`} stroke={color} strokeWidth="1.6" fill="none" />}
      {ev.accent && <text x={x - 4} y={TOP + 84} fontSize="13" fill={color}>&gt;</text>}
    </g>
  );
};

export const PlayAlongScore = ({ track, instrumentId, semitones, pos, start, onPick }) => {
  const score = useMemo(() => buildScore(track, instrumentId, semitones), [track, instrumentId, semitones]);
  const { bars, steps, sub, clef, perSystem, drums } = score;
  const systems = [];
  for (let i = 0; i < bars.length; i += perSystem) systems.push(bars.slice(i, i + perSystem).map((b, k) => ({ ...b, idx: i + k })));
  const barW = (W - LEFT - 10) / perSystem;
  const xAt = (st) => 30 + (st / steps) * (barW - 40);

  return (
    <div className="mt-5 rounded-2xl border border-border bg-background p-3 sm:p-4" data-testid="playalong-score">
      <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-primary">Score · {score.caption}</p>
      <svg viewBox={`0 0 ${W} ${systems.length * SYS_H}`} className="w-full">
        {systems.map((sys, si) => (
          <g key={si} transform={`translate(0 ${si * SYS_H})`}>
            {lineYs.map((y) => <line key={y} x1={10} x2={W - 10} y1={y} y2={y} stroke="var(--staff-line)" strokeWidth="1.1" />)}
            {clef === "treble" ? <text x={14} y={TOP + 56} fontSize="56" fill="var(--staff-note)" fontFamily="'Noto Music', serif">𝄞</text>
              : <g><rect x={22} y={TOP + 14} width={4} height={28} fill="var(--staff-note)" /><rect x={30} y={TOP + 14} width={4} height={28} fill="var(--staff-note)" /></g>}
            {sys.map((b, k) => {
              const bx = LEFT + k * barW;
              const active = pos.bar === b.idx;
              const bb = barBeams(b.events, clef, sub, drums, xAt, quarterLen(steps, sub));
              return (
                <g key={b.idx} transform={`translate(${bx} 0)`} data-testid={`score-bar-${b.idx}`}>
                  <rect x={2} y={TOP - 40} width={barW - 4} height={110} rx={8} fill={active ? "hsl(var(--primary) / 0.08)" : "transparent"}
                    className="cursor-pointer" onClick={() => onPick?.({ bar: b.idx, step: 0 })} data-testid={`score-bar-pick-${b.idx}`} />
                  {start && start.bar === b.idx && <line x1={xAt(start.step) - 12} x2={xAt(start.step) - 12} y1={TOP - 20} y2={TOP + 70} stroke="hsl(var(--primary))" strokeWidth="2" strokeDasharray="4 3" data-testid="score-start-marker" />}
                  {b.chords.map((c) => <text key={c.step} x={xAt(c.step)} y={TOP - 36} fontSize="13" fontWeight="600" fill="hsl(var(--primary))" fontFamily="monospace">{c.label}</text>)}
                  {b.events.map((ev, ei) => {
                    const on = active && pos.step >= ev.step && pos.step < ev.step + ev.len;
                    return (
                      <g key={ei} data-testid={on ? "score-note-active" : `score-note-${b.idx}-${ei}`} className="cursor-pointer" onClick={() => onPick?.({ bar: b.idx, step: ev.step })}>
                        <rect x={xAt(ev.step) - 12} y={TOP - 30} width={24} height={100} fill="transparent" />
                        <Note ev={ev} x={ev.bar ? xAt(steps / 2) : xAt(ev.step)} clef={clef} sub={sub} drums={drums} bm={bb.stems[ei]} color={on ? "hsl(var(--primary))" : "var(--staff-note)"} />
                      </g>
                    );
                  })}
                  {bb.beams.map((m, k) => { const t = m.up ? 4.5 : -4.5; return <polygon key={`bm${k}`} points={`${m.x0 - 0.7},${m.y0} ${m.x1 + 0.7},${m.y1} ${m.x1 + 0.7},${m.y1 + t} ${m.x0 - 0.7},${m.y0 + t}`} fill="var(--staff-note)" data-testid={`score-beam-${b.idx}-${k}`} />; })}
                  {active && <line x1={xAt(pos.step)} x2={xAt(pos.step)} y1={TOP - 8} y2={TOP + 64} stroke="hsl(var(--primary))" strokeWidth="1.5" opacity="0.6" />}
                  <line x1={barW} x2={barW} y1={TOP} y2={TOP + 56} stroke="var(--staff-line)" strokeWidth="1.4" />
                </g>
              );
            })}
          </g>
        ))}
      </svg>
    </div>
  );
};
